/**
 * 🔴🔴 QUEM ESTÁ DE COROA — a resposta que toda tela pode fazer.
 *
 * > **Matheus, 27/09/2026:** *"quando um usuário tá com a coroa no ranking, em
 * > TODO o sistema a coroa deverá aparecer nele também"*
 *
 * ⚠️ **A pergunta mudou de dono, e é por isso que este arquivo existe.**
 * Enquanto a coroa era do ranking, a tela que o desenhava tinha os valores na
 * mão e decidia sozinha. 🔴 **O card do Kanban não tem ranking nenhum** — e
 * carregar os quatro rankings inteiros em toda tela para desenhar um avatar de
 * 24px seria absurdo. ✅ Aqui a resposta é uma lista de ids, minúscula.
 *
 * ── Por que um CONTEXTO e não um hook solto em cada Avatar
 *
 * 🔴 **Um Kanban tem dezenas de avatares na tela.** Se cada um chamasse o hook,
 * seriam dezenas de assinaturas do `react-query` para a MESMA chave — ele
 * deduplica a requisição, mas não o trabalho de render. ✅ O provedor busca uma
 * vez e entrega um `Set`, que responde em tempo constante.
 *
 * ⚠️ **E sem o provedor nada quebra:** o contexto tem valor padrão VAZIO, então
 * uma tela que não o monte simplesmente não desenha coroa nenhuma. 🔴 Isso é de
 * propósito — *falha segura aqui é não coroar ninguém*, nunca coroar quem não
 * é. **Coroar a pessoa errada é uma afirmação falsa na cara de quem trabalha.**
 */
import { createContext, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api, hasToken } from '@/lib/api';
import { usePodeAcessar } from '@/lib/acesso-store';

interface RespostaCoroados {
  mesAno: string;
  userIds: string[];
}

/**
 * ⚠️ **`Set` vazio como padrão, e ele é a falha segura.** Uma tela sem o
 * provedor não desenha coroa — ❌ nunca desenha coroa errada.
 */
const ContextoDeCoroados = createContext<ReadonlySet<string>>(new Set());

export const ProvedorDeCoroados = ContextoDeCoroados.Provider;

/** Quem está coroado, para quem só quer perguntar. */
export function useEstaCoroado(userId: string | null | undefined): boolean {
  const coroados = useContext(ContextoDeCoroados);
  return !!userId && coroados.has(userId);
}

/**
 * Busca a lista no servidor.
 *
 * ⚠️ **`staleTime` longo de propósito:** a coroa muda quando alguém fecha uma
 * venda, não a cada segundo. 🔴 Uma consulta por minuto em toda tela do sistema
 * seria carga sem leitor — ninguém fica olhando um avatar esperando a coroa
 * aparecer.
 *
 * ⛔ **O parâmetro `mes` SAIU em 27/09/2026**, e vale escrever por que ele
 * existiu: a ideia era *"cada tela pede o mês que está olhando, e aí as duas
 * concordam"*. 🔴 **O Matheus decidiu o contrário, e o motivo é melhor:** o
 * Kanban, o comentário e a linha do tempo **não têm mês nenhum para pedir**.
 * Concordar só era possível se a coroa fosse **sempre a do mês corrente** —
 * *"independente do filtro que estiver escolhido em Indicadores"*.
 *
 * ✅ A função deixou de aceitar argumento: **não dá para passar o mês errado
 * num parâmetro que não existe.**
 */
export function useCoroados(): ReadonlySet<string> {
  // 🔴 BUGS #480 (03/10/2026): a rota mora atrás de `comercial.indicadores.ver`
  // (`server/index.ts`, mount de `/api/v1/crm/indicadores`). Chamada por quem
  // não tem a chave, ela respondia 403 em TODA tela — e o aviso global "Você não
  // tem acesso a esta parte da tela" aparecia para todo mundo fora do Comercial.
  // ✅ Quem não tem a chave não pergunta: o conjunto fica vazio (é o que o
  // servidor responderia, de qualquer forma). Enquanto as chaves carregam,
  // `usePodeAcessar` devolve `false` — a coroa só aparece um instante depois.
  const podeVerIndicadores = usePodeAcessar('comercial.indicadores.ver');
  const { data } = useQuery({
    queryKey: ['v1', 'crm', 'coroados', '(mês corrente)'] as const,
    queryFn: () => api.get<RespostaCoroados>('/api/v1/crm/indicadores/coroados'),
    enabled: hasToken() && podeVerIndicadores,
    staleTime: 5 * 60_000,
  });
  return new Set(podeVerIndicadores ? (data?.userIds ?? []) : []);
}
